// Meu plano (canvas B06): plano vigente, assinatura, ações (forma de pagamento, cancelar, manter, desistir),
// o que o plano tem e o histórico de cobranças.
import { useState } from 'react';
import { MESES_GRATIS_NO_ANUAL, NOME_DO_PLANO, type Plano, TENTOS_POR_MES, emReais, itensDoPlano, parcelaDoAnual, precoEmReais } from '@truco6/planos';
import { type CobrancaEu, NOME_DO_METODO, type ResumoAssinatura, api, dataCompleta, dataDoCiclo, diaMes, reais } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { compra, medir } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { EmBreve, Icone, Moeda, SeloEconomia, useEu } from './planos-comum.tsx';
import { CartaoSaldo } from './tentos-comum.tsx';
import { Carregando, Tela, Topo } from './comum.tsx';

const SELO: Record<ResumoAssinatura['status'], string> = { ativa: 'Ativo', cancelada: 'Cancelado', aguardando_pix: 'Aguardando Pix' };
const STATUS: Record<CobrancaEu['status'], string> = {
  paga: 'Pago', aberta: 'Em aberto', vencida: 'Vencida', anulada: 'Anulada', reembolsada: 'Devolvido',
};

type Confirmar = 'cancelar' | 'desistir' | null;

export function MeuPlano({ sessao, aoMudarPlano }: { sessao: Sessao; aoMudarPlano: (p: Plano) => void }) {
  const { eu, erro: erroEu, carregar } = useEu(sessao);
  const [confirmar, setConfirmar] = useState<Confirmar>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function fazer(fn: () => Promise<string | null>) {
    setErro(null); setOk(null); setOcupado(true);
    try {
      const msg = await fn();
      const novo = await carregar();
      if (novo) aoMudarPlano(novo.plano);
      setOk(msg); setConfirmar(null);
    } catch (e) { setErro((e as Error).message); } finally { setOcupado(false); }
  }

  if (!eu) {
    return erroEu ? (
      <Tela>
        <Topo titulo="Meu plano" voltar={voltar} />
        <p className="erro" role="alert">{erroEu}</p>
        <button className="btn2 largo" onClick={() => void carregar()}>Tentar de novo</button>
      </Tela>
    ) : <Carregando />;
  }

  const a = eu.assinatura;
  const plano: Plano = a?.plano ?? eu.plano;
  const nome = NOME_DO_PLANO[plano];
  const aberta = eu.cobrancas.find((c) => c.url);
  // Cortesia (código promocional): mostra até quando vale e não fala em renovação enquanto não houver cartão.
  const cortesia = !!a?.cortesia, ateCortesia = a?.cortesiaAte ? dataCompleta(a.cortesiaAte) : null;
  let linha: React.ReactNode = null;
  if (cortesia) linha = ateCortesia ? <><span>Cortesia até</span><b>{ateCortesia}</b></> : null;
  else if (a?.status === 'aguardando_pix') linha = <><span>Fatura em aberto</span><b>{aberta?.venceEm ? 'vence ' + diaMes(aberta.venceEm) : 'Pix'}</b></>;
  else if (a?.fimDoPeriodo) {
    const data = dataDoCiclo(a.fimDoPeriodo, a.ciclo);
    linha = a.cancelaNoFim ? <><span>Vale até</span><b>{data}</b></> : <><span>Próxima cobrança</span><b>{data}</b></>;
  }
  const fimDoPago = a?.ciclo === 'anual' ? 'o fim do ano pago' : 'o fim do mês pago';

  return (
    <Tela>
      <Topo titulo="Meu plano" voltar={voltar} />
      <div className="duas-colunas">
        <div className="col">
          <section className={'cartao-plano plano-' + plano} aria-label={'Plano ' + nome}>
            <div className="plano-cab">
              <Moeda plano={plano} tamanho={56} />
              <div>
                <h2>Plano {nome}</h2>
                <p className="nota">
                  {a && cortesia ? `${nome} de cortesia${ateCortesia ? ` até ${ateCortesia}` : ''}`
                    : a ? `${precoEmReais(a.plano, a.ciclo)}/${a.ciclo === 'anual' ? 'ano' : 'mês'} · ${a.ciclo === 'anual' ? 'Anual · ' : ''}${NOME_DO_METODO[a.metodo]}`
                    : plano === 'gratis' ? 'Sem mensalidade' : 'Cortesia do Truco de 6'}
                </p>
              </div>
              {a && <span className={'selo-status ' + a.status}>{SELO[a.status]}</span>}
            </div>
            {linha && <div className="linha-data"><Icone nome="calendario" />{linha}</div>}
          </section>

          <CartaoSaldo situacao={eu.tentos} para="loja" />
          {TENTOS_POR_MES[plano] > 0 && <p className="nota">O {nome} dá {TENTOS_POR_MES[plano]} Tentos a cada mês pago. Eles ficam com você mesmo se cancelar.</p>}

          {plano === 'gratis' && !a && (
            <button className="btn1 largo" onClick={() => ir({ tela: 'planos', plano: 'prata', ciclo: 'mensal' })}>VER PLANOS <small>a partir de {precoEmReais('prata')}/mês</small></button>
          )}
          {a && a.ciclo === 'mensal' && a.status === 'ativa' && !cortesia && (
            <section className="cartao convite-anual">
              <div className="plano-cab"><Moeda plano={a.plano} tamanho={40} /><p><b>Mude para o anual e economize {MESES_GRATIS_NO_ANUAL} meses</b>
                <span className="nota">{emReais(parcelaDoAnual(a.plano))}/mês no anual: {precoEmReais(a.plano, 'anual')} à vista por ano.</span></p></div>
              <SeloEconomia />
              <button className="btn2 largo dourado" onClick={() => ir({ tela: 'assinar', plano: a.plano, ciclo: 'anual' })}>Mudar para o anual <Icone nome="seta" tamanho={18} /></button>
            </section>
          )}
          {plano === 'prata' && a && a.ciclo === 'anual' && a.status === 'ativa' && (
            <section className="cartao convite-ouro">
              <div className="plano-cab"><Moeda plano="ouro" tamanho={40} /><p><b>Ouro: ligas, comunidades e campeonatos</b>
                <span className="nota">{precoEmReais('ouro', 'anual')} por ano. As ferramentas de organizador chegam em breve.</span></p></div>
              <button className="btn2 largo dourado" onClick={() => ir({ tela: 'planos', plano: 'ouro', ciclo: 'anual' })}>Conhecer o Ouro <Icone nome="seta" tamanho={18} /></button>
            </section>
          )}

          {ok && <p className="ok" role="status">{ok}</p>}
          {erro && <p className="erro" role="alert">{erro}</p>}

          {a && confirmar === null && (
            <ul className="cartao acoes-plano">
              {a.metodo === 'cartao' && a.status !== 'aguardando_pix' && !a.semCartao && (
                <li><button disabled={ocupado} onClick={() => fazer(async () => {
                  location.assign((await api<{ url: string }>(sessao, 'POST', '/assinatura/portal')).url);
                  return null;
                })}><Icone nome="cartao" /><span>Trocar forma de pagamento</span><Icone nome="seta" tamanho={18} /></button></li>
              )}
              {a.faturaAberta && (
                <li><a href={a.faturaAberta} target="_blank" rel="noopener"><Icone nome="pix" /><span>Pagar fatura em aberto</span><Icone nome="seta" tamanho={18} /></a></li>
              )}
              {a.status === 'ativa' && !cortesia && (
                <li><button onClick={() => ir({ tela: 'planos', plano: a.plano, ciclo: a.ciclo })}><Icone nome="renovar" /><span>Trocar de plano</span><Icone nome="seta" tamanho={18} /></button></li>
              )}
              {a.status === 'cancelada' && (
                <li><button disabled={ocupado} onClick={() => fazer(async () => {
                  await api(sessao, 'POST', '/assinatura/reativar');
                  medir('manter_assinatura', { plano: a.plano, ciclo: a.ciclo });
                  return a.semCartao ? `Pronto. O ${nome} de cortesia segue${ateCortesia ? ` até ${ateCortesia}` : ''}.` : `Pronto. O ${nome} segue renovando.`;
                })}><Icone nome="renovar" /><span>Manter assinatura</span><Icone nome="seta" tamanho={18} /></button></li>
              )}
              {a.status !== 'cancelada' && (
                <li><button className="discreto" disabled={ocupado} onClick={() => setConfirmar('cancelar')}>
                  <Icone nome="fechar" /><span>{a.status === 'aguardando_pix' ? 'Desistir deste Pix' : 'Cancelar assinatura'}</span><Icone nome="seta" tamanho={18} />
                </button></li>
              )}
              {a.podeDesistirAte && (
                <li><button className="discreto" disabled={ocupado} onClick={() => setConfirmar('desistir')}>
                  <Icone nome="volta" /><span>{a.valorPago > 0 ? 'Desistir e receber de volta' : 'Desistir do plano'}</span><Icone nome="seta" tamanho={18} />
                </button></li>
              )}
            </ul>
          )}

          {a && confirmar === 'cancelar' && (
            <section className="cartao">
              <p>{a.status === 'aguardando_pix'
                ? 'A fatura em aberto é anulada e você segue no Grátis.'
                : a.ciclo === 'anual'
                  ? `O ${nome} vale até ${a.fimDoPeriodo ? dataCompleta(a.fimDoPeriodo) : fimDoPago} e não renova. Não há devolução do ano pago.`
                  : `O ${nome} vale até ${a.fimDoPeriodo ? diaMes(a.fimDoPeriodo) : fimDoPago}. Depois volta para o Grátis.`}</p>
              <div className="dupla">
                <button className="btn2" disabled={ocupado} onClick={() => setConfirmar(null)}>Manter assinatura</button>
                <button className="btn2 perigo" disabled={ocupado} onClick={() => fazer(async () => {
                  const r = await api<{ ate: number | null }>(sessao, 'POST', '/assinatura/cancelar');
                  medir('cancelar_assinatura', { plano: a.plano, ciclo: a.ciclo, payment_type: a.metodo, pix_em_aberto: a.status === 'aguardando_pix' });
                  return r.ate ? `Assinatura cancelada. O ${nome} vale até ${dataDoCiclo(r.ate, a.ciclo)}.` : 'Pronto. Você segue no Grátis.';
                })}>Cancelar assinatura</button>
              </div>
            </section>
          )}

          {a && confirmar === 'desistir' && a.podeDesistirAte && (
            <section className="cartao">
              <p>{a.valorPago > 0
                ? `Devolvemos ${reais(a.valorPago)} pelo ${a.metodo === 'pix' ? 'Pix' : 'cartão'} e o ${nome} acaba agora.`
                : `O ${nome}${cortesia ? ' de cortesia' : ''} acaba agora e os Tentos que ele deu saem do saldo.`} Dá para desistir até {diaMes(a.podeDesistirAte)}.</p>
              <div className="dupla">
                <button className="btn2" disabled={ocupado} onClick={() => setConfirmar(null)}>Ficar com o {nome}</button>
                <button className="btn2 perigo" disabled={ocupado} onClick={() => fazer(async () => {
                  await api(sessao, 'POST', '/assinatura/desistir');
                  if (a.valorPago === 0) return 'Pronto. Você segue no Grátis.';
                  medir('refund', compra(a.plano, a.ciclo, { transaction_id: `${a.id}:${a.fimDoPeriodo ?? 0}`, value: a.valorPago / 100, payment_type: a.metodo }));
                  return 'Pronto. O valor volta em alguns dias e você segue no Grátis.';
                })}>{a.valorPago > 0 ? 'Desistir e receber' : 'Desistir'}</button>
              </div>
            </section>
          )}

        </div>
        <div className="col">
          <section aria-labelledby="h-tem">
            <h2 id="h-tem" className="rotulo">O que o {nome} tem</h2>
            <ul className="cartao itens">
              {itensDoPlano(plano).map((i) => (
                <li key={i.recurso} className={i.emBreve ? 'depois' : undefined}>
                  <Icone nome="checkCirculo" tamanho={20} />
                  <span>{i.texto}</span>
                  {i.emBreve && <EmBreve />}
                </li>
              ))}
            </ul>
          </section>
          {eu.cobrancas.length > 0 && (
            <section aria-labelledby="h-historico">
              <h2 id="h-historico" className="rotulo">Histórico de cobranças</h2>
              <ul className="cartao historico">
                {eu.cobrancas.map((c) => (
                  <li key={c.id}>
                    <p><b>{dataCompleta(c.data)}</b><span>{NOME_DO_PLANO[c.plano]} {c.ciclo} · {NOME_DO_METODO[c.metodo]}</span></p>
                    <b className="valor">{reais(c.valor)}</b>
                    {c.url ? <a className={'selo-cobranca ' + c.status} href={c.url} target="_blank" rel="noopener">Pagar</a>
                      : <span className={'selo-cobranca ' + c.status}>{STATUS[c.status]}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </Tela>
  );
}
